import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { useParams } from 'react-router';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { FlagContext } from 'libs.flags.react';
import { IrbErrorCode } from 'libs.irb.common';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { IRB_FLAGS } from '../utils';
export const useCreateIrbPackages = () => {
    const { studyId } = useParams();
    const { post } = useApiClient(ApiClientService.WEB);
    const { firebaseToken } = useContext(FirebaseAuthContext);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const flagManager = useContext(FlagContext);
    let extraParams = {};
    if (flagManager.getValue(IRB_FLAGS.irbEnableLenientRealAppScreens)) {
        extraParams = { realAppScreens: 'lenient' };
    }
    else if (flagManager.getValue(IRB_FLAGS.irbDisableRealAppScreens)) {
        extraParams = { realAppScreens: 'disabled' };
    }
    return useMutation({
        mutationKey: ['irb', 'create_packages'],
        mutationFn: async ({ packages, authToken, studyUuid, envId }) => {
            return await post(`/api/irb/create_package`, {
                studyId,
                studyUuid,
                packages,
                authToken,
                envId,
                ...extraParams,
            }, {
                // Pass firebase token, don't pass medable token
                withCredentials: false,
                headers: {
                    Authorization: `Bearer ${firebaseToken}`,
                },
                no500Handling: true,
            });
        },
        retry: false,
        onError: (error) => {
            if (error?.response?.status !== 500) {
                return;
            }
            const errorCode = error?.response?.data?.errorCode || error?.response?.data?.error;
            const errorMessage = error?.response?.data?.error;
            if (errorCode === IrbErrorCode.TRANSLATION_NOT_ENABLED_EN_US) {
                addNotification({
                    title: 'Translations not enabled in selected study version',
                    subtitle: (_jsxs("div", { className: 'flex flex-col gap-3', children: [_jsx("p", { className: 'm-0', children: 'Translations were not enabled when this study version was created. Even for English (en‑US) review packages, translations must be enabled.' }), _jsx("p", { className: 'm-0', children: _jsx("strong", { children: "To proceed, please:" }) }), _jsxs("ul", { className: 'list-disc pl-5 m-0 flex flex-col gap-1', children: [_jsxs("li", { children: ["Ensure translations are ", _jsx("strong", { children: "enabled" }), " for the study"] }), _jsxs("li", { children: ["Create a new ", _jsx("strong", { children: "study version" })] }), _jsxs("li", { children: ["Create or update a ", _jsx("strong", { children: "test deployment" }), " to use the new version"] })] })] })),
                    type: 'error',
                });
                return;
            }
            if (errorCode === IrbErrorCode.TRANSLATION_NOT_ENABLED) {
                addNotification({
                    title: 'Translations not enabled in selected study version',
                    subtitle: (_jsxs("div", { className: 'flex flex-col gap-3', children: [_jsx("p", { className: 'm-0', children: 'Translations were not enabled when this study version was created.' }), _jsx("p", { className: 'm-0', children: _jsx("strong", { children: "To proceed, please:" }) }), _jsxs("ul", { className: 'list-disc pl-5 m-0 flex flex-col gap-1', children: [_jsxs("li", { children: ["Ensure translations are ", _jsx("strong", { children: "enabled" }), " for the study"] }), _jsxs("li", { children: ["Create a new ", _jsx("strong", { children: "study version" })] }), _jsxs("li", { children: ["Create or update a ", _jsx("strong", { children: "test deployment" }), " to use the new version"] })] })] })),
                    type: 'error',
                });
                return;
            }
            const subtitle = {
                'Test plan screenshot API is in use, cannot proceed. Try again later': translate('Test plan screenshot API is in use, cannot proceed. Try again later'),
            }[errorMessage ?? ''] ||
                errorCode ||
                errorMessage;
            if (!subtitle) {
                return;
            }
            addNotification({
                title: translate('Cannot proceed'),
                subtitle,
                type: 'error',
            });
        },
    });
};
